import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { ShieldCheck, XCircle, Eye, Printer, Download, Loader2, ClipboardCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useMemo, useRef } from "react";
// xlsx loaded dynamically to reduce bundle size

export function VerificationQueue() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [notes, setNotes] = useState("");
  const [previewBatch, setPreviewBatch] = useState<any | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  // Fetch my pending verifications
  const { data: myVerifications = [], isLoading: loadingVerifications } = useQuery({
    queryKey: ["my-verifications", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exam_batch_verifications")
        .select("*")
        .eq("assigned_to", user!.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  // Fetch the associated batches
  const batchIds = useMemo(() => myVerifications.map((v) => v.batch_id), [myVerifications]);

  const { data: batches = [] } = useQuery({
    queryKey: ["verification-batches", batchIds, user?.id],
    queryFn: async () => {
      if (batchIds.length === 0) return [];
      const { data, error } = await supabase
        .from("exam_batches")
        .select("*")
        .in("id", batchIds);
      if (error) throw error;
      return data;
    },
    enabled: batchIds.length > 0,
  });

  const batchMap = useMemo(() => new Map(batches.map((b) => [b.id, b])), [batches]);

  // Preview results
  const { data: previewResults = [], isLoading: previewLoading } = useQuery({
    queryKey: ["verification-preview", previewBatch?.id],
    enabled: !!previewBatch,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exam_results")
        .select("*, courses(course_name, course_code)")
        .eq("batch_id", previewBatch.id)
        .order("form_iv_index");
      if (error) throw error;
      return data;
    },
  });

  const previewGrouped = useMemo(() => {
    if (!previewResults.length) return [];
    const map = new Map<string, { name: string; formIv: string; modules: any[]; gpa: string; overallRemark: string }>();
    for (const r of previewResults) {
      const key = r.form_iv_index || r.student_id || r.id;
      if (!map.has(key)) {
        map.set(key, { name: key, formIv: r.form_iv_index || "", modules: [], gpa: r.gpa || "", overallRemark: r.overall_remark || "" });
      }
      const entry = map.get(key)!;
      const examParts = (r.exam_type || "").split(" - ");
      const moduleCode = examParts.length > 1 ? examParts.slice(1).join(" - ") : r.exam_type;
      entry.modules.push({
        code: moduleCode,
        grade: r.grade || (r.marks != null ? String(r.marks) : ""),
        remark: r.remark || "",
      });
    }
    return Array.from(map.values());
  }, [previewResults]);

  // Complete verification via secure RPC
  const respondMutation = useMutation({
    mutationFn: async ({ verificationId, action }: { verificationId: string; batchId: string; action: "verified" | "rejected" }) => {
      const { error } = await (supabase as any).rpc("complete_exam_batch_verification", {
        _verification_id: verificationId,
        _action: action,
        _notes: notes || null,
      });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["my-verifications"] });
      queryClient.invalidateQueries({ queryKey: ["verification-batches"] });
      queryClient.invalidateQueries({ queryKey: ["exam-batches"] });
      queryClient.invalidateQueries({ queryKey: ["exam-batch-verifications"] });
      queryClient.invalidateQueries({ queryKey: ["module-visibility"] });
      toast({
        title: variables.action === "verified" ? "Verification confirmed" : "Verification rejected",
        description: variables.action === "verified" ? "Results are now verified and ready for publishing." : "Results sent back for review.",
      });
      setNotes("");
    },
    onError: (e: any) => {
      toast({ title: "Error", description: e.message, variant: "destructive" });
      // Refetch verifications on error to clear stale data
      queryClient.invalidateQueries({ queryKey: ["my-verifications"] });
      queryClient.invalidateQueries({ queryKey: ["verification-batches"] });
    },
  });

  const handleDownloadExcel = async (batch: any) => {
    try {
      const { data, error } = await supabase
        .from("exam_results")
        .select("*")
        .eq("batch_id", batch.id)
        .order("form_iv_index");
      if (error) throw error;
      if (!data || data.length === 0) {
        toast({ title: "No results", description: "No results found for this batch.", variant: "destructive" });
        return;
      }

      const studentMap = new Map<string, any>();
      const moduleCodes = new Set<string>();
      for (const r of data) {
        const key = r.form_iv_index || r.student_id || r.id;
        if (!studentMap.has(key)) {
          studentMap.set(key, { studentId: key, formIv: r.form_iv_index || "", gpa: r.gpa || "", remarks: r.overall_remark || r.remark || "", modules: {} });
        }
        const examParts = (r.exam_type || "").split(" - ");
        const code = examParts.length > 1 ? examParts.slice(1).join(" - ") : r.exam_type || "";
        moduleCodes.add(code);
        studentMap.get(key)!.modules[code] = r.grade || (r.marks != null ? r.marks : "");
      }

      const codes = Array.from(moduleCodes).sort();
      const rows: any[] = [];
      let sn = 1;
      for (const [, stu] of studentMap) {
        const row: any = { SN: sn++, "Student ID": stu.studentId, "Form IV Index": stu.formIv };
        for (const c of codes) row[c] = stu.modules[c] || "";
        row["GPA"] = stu.gpa;
        row["Remarks"] = stu.remarks;
        rows.push(row);
      }

      const XLSX = await import("xlsx");
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Results");
      XLSX.writeFile(wb, `${batch.class}_${batch.semester}_${batch.exam_type}.xlsx`);
    } catch (e: any) {
      toast({ title: "Download failed", description: e.message, variant: "destructive" });
    }
  };

  const handlePrint = (batch: any) => {
    setPreviewBatch(batch);
    setTimeout(() => window.print(), 1000);
  };

  if (loadingVerifications) {
    return (
      <Card className="border-purple-200 bg-purple-50/30 dark:bg-purple-950/10 dark:border-purple-900/30">
        <CardContent className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (myVerifications.length === 0) return null;

  return (
    <>
      <Card className="border-purple-200 bg-purple-50/30 dark:bg-purple-950/10 dark:border-purple-900/30">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base text-purple-700 dark:text-purple-300">
            <ClipboardCheck className="h-4 w-4" />
            My Verification Queue
          </CardTitle>
          <CardDescription>
            You have {myVerifications.length} batch{myVerifications.length > 1 ? "es" : ""} assigned for verification
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Class</TableHead>
                  <TableHead>Semester</TableHead>
                  <TableHead>Exam Type</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead>Students</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Tools</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {myVerifications.map((v) => {
                  const batch = batchMap.get(v.batch_id);
                  if (!batch) return null;
                  return (
                    <TableRow key={v.id}>
                      <TableCell className="font-medium">{batch.class}</TableCell>
                      <TableCell>{batch.semester}</TableCell>
                      <TableCell>{batch.exam_type}</TableCell>
                      <TableCell>{batch.academic_year}</TableCell>
                      <TableCell>{batch.total_students}</TableCell>
                      <TableCell><StatusBadge status={batch.status} /></TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Preview" onClick={() => setPreviewBatch(batch)}>
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Print" onClick={() => handlePrint(batch)}>
                            <Printer className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Download" onClick={() => handleDownloadExcel(batch)}>
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 flex-wrap">
                          <Textarea
                            placeholder="Notes..."
                            className="h-8 text-xs w-36 min-h-0"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-700"
                            onClick={() => respondMutation.mutate({ verificationId: v.id, batchId: v.batch_id, action: "verified" })}
                            disabled={respondMutation.isPending}
                          >
                            <ShieldCheck className="mr-1 h-3 w-3" /> Confirm
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-700"
                            onClick={() => respondMutation.mutate({ verificationId: v.id, batchId: v.batch_id, action: "rejected" })}
                            disabled={respondMutation.isPending}
                          >
                            <XCircle className="mr-1 h-3 w-3" /> Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Preview dialog */}
      <Dialog open={!!previewBatch} onOpenChange={(open) => !open && setPreviewBatch(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Results Preview — {previewBatch?.class} · {previewBatch?.semester} · {previewBatch?.exam_type}
            </DialogTitle>
          </DialogHeader>
          <div ref={printRef}>
            {previewLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : previewGrouped.length === 0 ? (
              <div className="text-center py-8 space-y-2">
                <p className="text-muted-foreground">No results found in this batch</p>
                <p className="text-xs text-muted-foreground">This batch contains no uploaded result records. Please contact Examination to re-upload.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Modules &amp; Grades</TableHead>
                    <TableHead>GPA</TableHead>
                    <TableHead>Remark</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewGrouped.map((s, idx) => (
                    <TableRow key={s.formIv || idx}>
                      <TableCell className="text-xs">{idx + 1}</TableCell>
                      <TableCell className="text-xs font-medium">{s.formIv || s.name}</TableCell>
                      <TableCell className="text-xs">
                        {s.modules.map((m) => (
                          <span key={m.code} className="inline-block mr-2">
                            <span className="font-mono">{m.code}</span>: {m.grade}
                            {m.remark && <span className="text-muted-foreground ml-0.5">({m.remark})</span>}
                          </span>
                        ))}
                      </TableCell>
                      <TableCell className="text-xs font-medium">{s.gpa}</TableCell>
                      <TableCell className="text-xs">{s.overallRemark}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
